/*
 * Typeset a quiz's <m> math (bhs-content's quiz-xml.ts) in content that was
 * inserted AFTER page load — an answer-key gloss, a speed quiz's next card.
 * MathJax's own startup pass covers whatever is in the DOM when it finishes
 * loading; anything added later needs this.
 *
 * The script is the CDN MathJax 3 that views/partials/mathjax.njk loads, so
 * every call is optional: on a page without the partial, or before the async
 * script has arrived, there is nothing to call and nothing to do. Loading
 * MathJax from here instead would mean shipping it, and the untypeset
 * fallback is the TeX source, which reads fine.
 */

interface MathJax {
  typesetPromise?: (elements?: Element[]) => Promise<void>;
  startup?: { promise?: Promise<unknown> };
}

const mathJax = (): MathJax | undefined => (window as { MathJax?: MathJax }).MathJax;

export const typesetMath = (...elements: Element[]): void => {
  const mj = mathJax();
  if (!mj) return; // not loaded yet: the startup pass will find this content

  // Failing to typeset leaves the TeX source on the page — degraded but
  // perfectly readable — so this is the one place a swallowed rejection
  // beats an error bar over the student's quiz.
  const typeset = () =>
    mathJax()
      ?.typesetPromise?.(elements.length ? elements : undefined)
      .catch(() => {});

  // Mid-startup, typesetPromise may not exist yet but startup.promise does;
  // chaining off it is MathJax's documented way to queue behind the boot.
  if (mj.typesetPromise) typeset();
  else if (mj.startup?.promise) void mj.startup.promise.then(typeset, () => {});
};
